import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  /** @type {boolean} */
  #pendingAcierAdd = false;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = (event) => {
    this.#cachedContent.clear();

    if (!this.#pendingAcierAdd) return;
    this.#pendingAcierAdd = false;

    const modalContent = document.getElementById('quick-add-modal-content');
    const addButton = modalContent?.querySelector('.acier-quick-buy__add');
    const status = modalContent?.querySelector('.acier-quick-buy__status');
    const didError = Boolean(event?.detail?.data?.didError);

    if (didError) {
      if (addButton instanceof HTMLButtonElement) {
        addButton.disabled = false;
        addButton.textContent = 'Ajouter au panier';
      }
      if (status instanceof HTMLElement) {
        status.textContent = 'Impossible d’ajouter ce produit. Vérifiez la variante et réessayez.';
        status.dataset.available = 'false';
      }
      return;
    }

    this.#closeQuickAddModal();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    const acierProduct = this.#getAcierProductData();

    if (acierProduct && this.#renderAcierQuickAdd(acierProduct)) {
      this.#openQuickAddModal();
      return;
    }

    // Native Horizon fallback for any non-Acier product card.
    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #getAcierProductData() {
    const script = this.querySelector('[data-quick-add-product-json]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('Acier Shop quick add: invalid product data', error);
      return null;
    }
  }

  #formatAcierMoney(cents) {
    const amount = Number(cents || 0) / 100;
    const locale = document.documentElement.lang || 'fr-FR';
    const currency = window.Shopify?.currency?.active || 'EUR';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch {
      return amount.toFixed(2).replace('.', ',') + ' €';
    }
  }

  #normalizeAcierMediaText(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/×/g, 'x')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  #findAcierMedia(product, selectedOptions, variant) {
    if (variant?.image) return variant.image;

    const joined = this.#normalizeAcierMediaText((selectedOptions || []).join(' '));
    const dimensions = joined.match(/\b\d{3,4}\s*x\s*\d{3,4}\b/g) || [];
    const rals = joined.match(/\b(?:9005|7016|9010)\b/g) || [];
    const millimeters = joined.match(/\b\d{3,4}\s*mm\b/g) || [];
    const colors = ['noir', 'gris', 'blanc'].filter((color) => joined.includes(color));

    let bestImage = null;
    let bestScore = 0;

    for (const media of product.media || []) {
      if (!media?.image) continue;
      const alt = this.#normalizeAcierMediaText(media.alt);
      let score = 0;

      rals.forEach((ral) => {
        if (alt.includes(ral)) score += 12;
      });
      colors.forEach((color) => {
        if (alt.includes(color)) score += 8;
      });
      dimensions.forEach((dimension) => {
        if (alt.includes(dimension)) score += 6;
      });
      millimeters.forEach((measurement) => {
        if (alt.includes(measurement)) score += 3;
      });

      if (score > bestScore) {
        bestScore = score;
        bestImage = media.image;
      }
    }

    return bestImage || product.featured_image || product.media?.find((media) => media?.image)?.image || null;
  }

  #renderAcierQuickAdd(product) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent || !product?.variants?.length) return false;

    const initialVariantId = this.#getSelectedVariantId();
    let currentVariant =
      product.variants.find((variant) => String(variant.id) === String(initialVariantId)) ||
      product.variants.find((variant) => variant.available) ||
      product.variants[0];

    let selectedOptions = [...(currentVariant.options || [])];
    let quantity = Math.max(Number(currentVariant.minimum || 1), 1);

    const shell = document.createElement('div');
    shell.className = 'acier-quick-buy';

    const mediaWrap = document.createElement('div');
    mediaWrap.className = 'acier-quick-buy__media';

    const image = document.createElement('img');
    image.className = 'acier-quick-buy__image';
    image.loading = 'eager';
    image.alt = product.title || '';
    mediaWrap.appendChild(image);

    const panel = document.createElement('div');
    panel.className = 'acier-quick-buy__panel';

    const eyebrow = document.createElement('p');
    eyebrow.className = 'acier-quick-buy__eyebrow';
    eyebrow.textContent = 'AJOUT RAPIDE';

    const title = document.createElement('a');
    title.className = 'acier-quick-buy__title';
    title.href = product.url || this.productPageUrl;
    title.textContent = product.title || this.dataset.productTitle || '';

    const priceRow = document.createElement('div');
    priceRow.className = 'acier-quick-buy__price-row';

    const price = document.createElement('span');
    price.className = 'acier-quick-buy__price';

    const compare = document.createElement('span');
    compare.className = 'acier-quick-buy__compare';

    priceRow.append(price, compare);

    const optionsWrap = document.createElement('div');
    optionsWrap.className = 'acier-quick-buy__options';

    const quantityWrap = document.createElement('div');
    quantityWrap.className = 'acier-quick-buy__quantity';

    const quantityLabel = document.createElement('span');
    quantityLabel.className = 'acier-quick-buy__quantity-label';
    quantityLabel.textContent = 'Quantité';

    const quantityControl = document.createElement('div');
    quantityControl.className = 'acier-quick-buy__quantity-control';

    const minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'acier-quick-buy__qty-button';
    minus.setAttribute('aria-label', 'Diminuer la quantité');
    minus.textContent = '−';

    const qtyInput = document.createElement('input');
    qtyInput.type = 'number';
    qtyInput.className = 'acier-quick-buy__qty-input';
    qtyInput.min = '1';
    qtyInput.step = '1';
    qtyInput.inputMode = 'numeric';

    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'acier-quick-buy__qty-button';
    plus.setAttribute('aria-label', 'Augmenter la quantité');
    plus.textContent = '+';

    quantityControl.append(minus, qtyInput, plus);
    quantityWrap.append(quantityLabel, quantityControl);

    const status = document.createElement('p');
    status.className = 'acier-quick-buy__status';
    status.setAttribute('aria-live', 'polite');

    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'button acier-quick-buy__add';

    const details = document.createElement('a');
    details.className = 'acier-quick-buy__details';
    details.href = product.url || this.productPageUrl;
    details.textContent = 'Voir la fiche produit →';

    panel.append(eyebrow, title, priceRow, optionsWrap, quantityWrap, status, addButton, details);
    shell.append(mediaWrap, panel);

    const findVariant = () =>
      product.variants.find((variant) =>
        (variant.options || []).every((option, index) => option === selectedOptions[index])
      );

    const canValueMatch = (optionIndex, value) =>
      product.variants.some((variant) => {
        if (!variant.available) return false;
        if (variant.options?.[optionIndex] !== value) return false;

        return (variant.options || []).every((option, index) => {
          if (index === optionIndex) return true;
          return selectedOptions[index] == null || option === selectedOptions[index];
        });
      });

    const renderOptions = () => {
      optionsWrap.replaceChildren();

      (product.options || []).forEach((option, optionIndex) => {
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'acier-quick-buy__option';

        const legend = document.createElement('legend');
        legend.className = 'acier-quick-buy__option-heading';

        const optionName = document.createElement('span');
        optionName.textContent = option.name || 'Option';

        const selected = document.createElement('span');
        selected.className = 'acier-quick-buy__selected';
        selected.textContent = selectedOptions[optionIndex] || '';

        legend.append(optionName, selected);

        const values = document.createElement('div');
        values.className = 'acier-quick-buy__values';

        (option.values || []).forEach((value) => {
          const input = document.createElement('input');
          const inputId = `AcierQuickBuy-${product.id}-${optionIndex}-${String(value).replace(/[^a-zA-Z0-9]+/g, '-')}`;
          input.type = 'radio';
          input.className = 'acier-quick-buy__radio';
          input.name = `AcierQuickBuy-${product.id}-${optionIndex}`;
          input.id = inputId;
          input.value = value;
          input.checked = selectedOptions[optionIndex] === value;

          const isPossible = canValueMatch(optionIndex, value);
          input.disabled = !isPossible;

          const label = document.createElement('label');
          label.className = 'acier-quick-buy__pill';
          label.htmlFor = inputId;
          label.textContent = value;
          if (!isPossible) label.setAttribute('aria-disabled', 'true');

          input.addEventListener('change', () => {
            if (!input.checked) return;
            selectedOptions[optionIndex] = value;
            currentVariant = findVariant();

            // If the exact combination does not exist, keep the changed value and
            // select the first available compatible variant to avoid a dead end.
            if (!currentVariant) {
              currentVariant = product.variants.find(
                (variant) => variant.available && variant.options?.[optionIndex] === value
              );
              if (currentVariant) selectedOptions = [...currentVariant.options];
            }

            renderAll();
          });

          values.append(input, label);
        });

        fieldset.append(legend, values);
        optionsWrap.appendChild(fieldset);
      });
    };

    const renderState = () => {
      currentVariant = findVariant() || currentVariant;
      const available = Boolean(currentVariant?.available);

      price.textContent = this.#formatAcierMoney(currentVariant?.price || 0);

      if (currentVariant?.compare_at_price && currentVariant.compare_at_price > currentVariant.price) {
        compare.textContent = this.#formatAcierMoney(currentVariant.compare_at_price);
        compare.hidden = false;
      } else {
        compare.textContent = '';
        compare.hidden = true;
      }

      const nextImage = this.#findAcierMedia(product, selectedOptions, currentVariant);
      if (nextImage) {
        image.src = nextImage;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.hidden = true;
      }

      const minimum = Math.max(Number(currentVariant?.minimum || 1), 1);
      if (quantity < minimum) quantity = minimum;
      qtyInput.min = String(minimum);
      qtyInput.value = String(quantity);

      status.textContent = available ? 'En stock · prêt à être ajouté au panier' : 'Cette combinaison est indisponible';
      status.dataset.available = available ? 'true' : 'false';

      addButton.disabled = !available;
      addButton.textContent = available ? 'Ajouter au panier' : 'Indisponible';
    };

    const renderAll = () => {
      renderOptions();
      renderState();
    };

    minus.addEventListener('click', () => {
      const min = Math.max(Number(qtyInput.min || 1), 1);
      quantity = Math.max(min, Number(qtyInput.value || quantity) - 1);
      qtyInput.value = String(quantity);
    });

    plus.addEventListener('click', () => {
      quantity = Math.max(1, Number(qtyInput.value || quantity) + 1);
      qtyInput.value = String(quantity);
    });

    qtyInput.addEventListener('change', () => {
      const min = Math.max(Number(qtyInput.min || 1), 1);
      quantity = Math.max(min, Number.parseInt(qtyInput.value || String(min), 10) || min);
      qtyInput.value = String(quantity);
    });

    addButton.addEventListener('click', () => {
      currentVariant = findVariant();
      if (!currentVariant?.available) return;

      const productFormComponent = this.querySelector('product-form-component');
      const form = productFormComponent?.querySelector('form[data-type="add-to-cart-form"]');
      const variantInput = form?.querySelector('input[name="id"]');
      const quantityInput = form?.querySelector('input[name="quantity"]');
      const nativeAddButton = form?.querySelector('.quick-add__button--add');

      if (!(form instanceof HTMLFormElement) || !(variantInput instanceof HTMLInputElement)) {
        status.textContent = 'Impossible d’ajouter ce produit. Ouvrez la fiche produit.';
        status.dataset.available = 'false';
        return;
      }

      variantInput.disabled = false;
      variantInput.value = String(currentVariant.id);

      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.value = String(quantity);
      }

      if (nativeAddButton instanceof HTMLButtonElement) {
        nativeAddButton.disabled = false;
      }

      addButton.disabled = true;
      addButton.textContent = 'Ajout…';
      status.textContent = 'Ajout au panier en cours…';
      status.dataset.available = 'true';
      this.#pendingAcierAdd = true;

      form.requestSubmit(nativeAddButton instanceof HTMLButtonElement ? nativeAddButton : undefined);

      window.setTimeout(() => {
        if (!this.#pendingAcierAdd) return;
        this.#pendingAcierAdd = false;

        if (document.getElementById('quick-add-dialog')?.querySelector('dialog[open]')) {
          renderState();
          status.textContent = 'La réponse du panier tarde. Vous pouvez réessayer.';
          status.dataset.available = 'false';
        }
      }, 3500);
    });

    modalContent.replaceChildren(shell);
    renderAll();

    return true;
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
